import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssetService from "../../../apis/AssetService";

const DEFAULT_ALLOWED_TYPES = [
  "image/*",
  "video/*",
  "audio/*",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];

const AssetUploadDialog = ({ open, onClose, onUploaded, existingTags = [] }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [path, setPath] = useState("");
  const [tags, setTags] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const allTagOptions = useMemo(() => {
    const unique = new Set([...(existingTags || []), ...(tags || [])]);
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [existingTags, tags]);

  const handleFileChange = (event) => {
    const [selectedFile] = event.target.files || [];
    setFile(selectedFile || null);
    setError(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const [droppedFile] = event.dataTransfer.files || [];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const resetState = () => {
    setFile(null);
    setTags([]);
    setTitle("");
    setDescription("");
    setPath("");
    setUploadProgress(null);
    setIsSubmitting(false);
    setError(null);
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    resetState();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!file) {
      setError("Choose a file to upload.");
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    setError(null);

    try {
      const trimmedPath = path.trim();
      const normalizedPath =
        trimmedPath.length > 0
          ? trimmedPath
              .split(/[\\/]+/)
              .filter((segment) => segment.length > 0)
              .join("/")
          : "";

      const payload = {
        fileName: file.name,
        contentType: file.type || "application/octet-stream",
        tags,
        path: normalizedPath || undefined,
      };

      const created = await AssetService.createAsset(payload);

      await AssetService.uploadAsset(created.uploadUrl, file, {
        onUploadProgress: (progressEvent) => {
          if (!progressEvent?.total) {
            return;
          }

          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      });

      const updatePayload = {
        status: "UPLOADED",
        size: file.size,
        tags,
        title: title || undefined,
        description: description || undefined,
        path: normalizedPath || undefined,
      };

      const updated = await AssetService.updateAsset(created.id, updatePayload);

      onUploaded?.(updated || { ...created, ...updatePayload });
      resetState();
      onClose?.();
    } catch (uploadError) {
      console.error("Failed to upload asset", uploadError);
      setError(
        uploadError?.response?.data?.message ||
          uploadError?.message ||
          "Unable to upload the asset right now."
      );
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload new asset</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack spacing={2}>
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                bgcolor: "background.default",
                cursor: "pointer",
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CloudUploadIcon color="primary" sx={{ fontSize: 40 }} />
                <div>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Drag & drop a file here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse your computer
                  </Typography>
                </div>
                <input
                  type="file"
                  accept={DEFAULT_ALLOWED_TYPES.join(",")}
                  style={{ display: "none" }}
                  id="asset-upload-input"
                  onChange={handleFileChange}
                />
                <label htmlFor="asset-upload-input">
                  <Button variant="outlined" component="span">
                    Choose file
                  </Button>
                </label>
                {file && (
                  <Typography variant="body2" color="text.secondary">
                    Selected: {file.name}
                  </Typography>
                )}
              </Stack>
            </Box>
            <TextField
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              multiline
              minRows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              fullWidth
            />
            <TextField
              label="Path"
              value={path}
              onChange={(event) => setPath(event.target.value)}
              helperText="Optional folder path appended to the S3 bucket before the file name."
              fullWidth
            />
            <Autocomplete
              multiple
              freeSolo
              options={allTagOptions}
              value={tags}
              onChange={(_, newTags) => setTags(newTags)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  helperText="Press enter to add a tag."
                />
              )}
            />
            {uploadProgress !== null && (
              <Stack spacing={1}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary" textAlign="right">
                  {uploadProgress}%
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || !file}
          >
            {isSubmitting ? "Uploading…" : "Upload"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AssetUploadDialog;
